// admin-terms.js - curation panel for the SEARCH TERM INDEX (spec_term_index.txt section 10).
//
// WHAT THIS IS FOR
// The dashboard search box used to run on three hand-authored lists inside js/gdbs-search.js. Those
// are gone: terms are now PARSED out of the repo, every one of them carrying the source kind and the
// exact file it was read from. Parsing is deterministic and it is also blind - it cannot know that a
// person hunting for Worklog types "timesheet", and it will happily mint a term from a citation that
// says nothing about what a tool does. So the parse needs a human editor, and this is that editor.
//
// THE ONE RULE THIS PANEL EXISTS TO ENFORCE VISIBLY
// An admin-authored term must never be mistakable for a parsed one. Spec section 3 states it and this
// UI is where a human could otherwise be fooled: every row carries an ORIGIN badge, admin rows are
// accented and carry the override file as their provenance, and parsed rows show the real file they
// came out of. A term whose origin you cannot see is a term you cannot trust, which would defeat the
// entire point of replacing the hand lists.
//
// WHY IT STILL WORKS WITH NO SERVER
// The API in spec section 7 (GET/POST/DELETE /api/terms...) may not be deployed. When it is absent
// this panel loads the committed artifact plus the override seed and runs READ-ONLY, says so in the
// banner, and offers the edited override as copyable JSON for the seed file. A degraded editor that
// tells you it is degraded beats a blank tab, and it beats a Save button that silently does nothing.

import { api } from './api.js';

// Static fallbacks - the same two files js/gdbs-search.js falls back to, so what an admin sees here is
// exactly what the search box is running on when the API is down.
const STATIC_INDEX_URL = '/data/term-index.json';
const STATIC_SEED_URL = '/data/term-overrides.seed.json';

const STATE = {
  entities: [],        // parsed entities: { entityKind, entityId, tier, name, nav, terms[], droppedTermCount }
  overrides: {},       // key -> { add[], suppress[], boost{} } as loaded
  labels: {},          // key -> { label, desc }  (presentation only, see the seed file's readme)
  meta: null,          // IndexMeta
  live: false,         // true when /api/terms answered, i.e. edits can actually be saved
  emptyServerIndex: false, // API up, but holding no entities yet - a different problem from API down
  metaNote: '',        // verbatim { ok:false, reason } from GET /api/terms/meta, shown as-is
  selected: null,      // key of the entity being edited
  draft: null,         // working copy of the selected entity's override
  filter: '',
};

function esc(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const keyOf = (e) => e.entityKind + ':' + e.entityId;

// Same tokenizer contract as gdbs-search.js and the build script: an admin cannot enter a term the
// matcher could never see. Lowercase, any non-alphanumeric run becomes a break, and a term is a SINGLE
// token - "time tracking" is entered as two terms, because that is how the index stores and matches.
function tokenizeInput(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean);
}

async function fetchStatic(url) {
  try {
    const r = await fetch(url, { cache: 'no-cache' });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

// ---------------------------------------------------------------------------------------------
// LOAD
// API first, artifact second - the same order the search box uses, so the panel never shows a
// vocabulary different from the one answering real queries.
// ---------------------------------------------------------------------------------------------
async function load() {
  const detail = document.getElementById('terms-detail');
  if (detail) detail.innerHTML = '<p class="terms-hint">Loading term index...</p>';

  let idx = null;
  STATE.live = false;
  STATE.emptyServerIndex = false;
  try {
    const res = await api.termsIndex();
    // An `entities` ARRAY - even an empty one - means the controller answered. That distinction
    // matters: "the API is not deployed" and "the API is up but nobody has ingested a build yet" look
    // identical if you only test for content, and they need completely different actions from an
    // operator (deploy the controller vs press Re-ingest artifact).
    if (res && Array.isArray(res.entities)) {
      STATE.live = true;
      if (res.entities.length) idx = res;
      else STATE.emptyServerIndex = true;
    }
  } catch { /* fall through to the artifact; an absent API is a state, not a crash */ }

  if (!idx) idx = await fetchStatic(STATIC_INDEX_URL);

  if (!idx || !Array.isArray(idx.entities)) {
    STATE.entities = [];
    STATE.meta = null;
    renderMeta();
    if (detail) {
      detail.innerHTML = '<p class="terms-hint"><b>The term index is empty.</b> '
        + '<code>/api/terms</code> is holding no entities and there is no browser-served fallback by design '
        + '(the artifact lives in <code>App_Data</code> on the server). To populate it: rebuild locally with '
        + '<code>node scripts/tools/build-term-index.mjs</code>, deploy <code>App_Data/term_index_seed.json</code>, '
        + 'then press <b>Re-ingest artifact</b> (or restart the app - the boot bootstrap loads the seed when the '
        + 'store is empty). If even that fails, confirm the term API controller is deployed.</p>';
    }
    renderList();
    return;
  }

  STATE.entities = idx.entities.slice().sort((a, b) => (a.entityKind + a.entityId).localeCompare(b.entityKind + b.entityId));

  // Meta: prefer the dedicated endpoint (spec section 7), fall back to the block carried on the
  // artifact itself. Both describe the same build, so a mismatch would itself be worth seeing.
  STATE.meta = {
    builderVersion: idx.builderVersion || '', builtAt: idx.builtAt || '',
    entityCount: idx.entityCount || idx.entities.length, termCount: idx.termCount || 0,
    droppedCount: idx.droppedCount || 0, sourceCounts: idx.sourceCounts || {},
  };
  if (STATE.live) {
    try {
      // GET /api/terms/meta is admin-gated and answers in the controller's envelope:
      // { ok:true, result: IndexMeta } on success, { ok:false, reason } for a never-built index.
      // The reason is worth surfacing verbatim rather than falling back silently to the artifact's
      // own numbers, which would describe a build the server may not be holding.
      const m = await api.termsMeta();
      if (m && m.ok && m.result) STATE.meta = Object.assign(STATE.meta, m.result);
      else if (m && m.ok === false && m.reason) STATE.metaNote = m.reason;
    } catch { /* meta is a nicety; the index already carries the same fields */ }
  }

  // Overrides. When the API served the index they are ALREADY resolved into it, so the seed is loaded
  // only to show what curation exists and to keep the read-only editor usable.
  const seed = await fetchStatic(STATIC_SEED_URL);
  STATE.overrides = (seed && seed.overrides) || {};
  STATE.labels = (seed && seed.labels) || {};

  renderMeta();
  renderList();
  if (STATE.selected) select(STATE.selected); else renderDetail();
}

// ---------------------------------------------------------------------------------------------
// META STRIP - staleness at a glance (spec section 10).
// ---------------------------------------------------------------------------------------------
function renderMeta() {
  const el = document.getElementById('terms-meta');
  if (!el) return;
  if (!STATE.meta) { el.innerHTML = '<span class="terms-pill is-warn">no index loaded</span>'; return; }
  const m = STATE.meta;

  // Age in whole days. An index built before the last plugin was added is describing a product that no
  // longer exists, and the only honest way to show that is the actual number.
  let age = '';
  const built = m.builtAt ? Date.parse(m.builtAt) : NaN;
  if (!Number.isNaN(built)) {
    const days = Math.floor((Date.now() - built) / 86400000);
    age = days <= 0 ? 'today' : (days === 1 ? '1 day ago' : days + ' days ago');
  }
  const stale = !Number.isNaN(built) && (Date.now() - built) > 30 * 86400000;

  const srcRows = Object.keys(m.sourceCounts || {}).sort()
    .map((k) => `<span class="terms-src"><b>${esc(k)}</b> ${Number(m.sourceCounts[k]) || 0}</span>`).join('');

  el.innerHTML = `
    <div class="terms-meta-row">
      <span class="terms-pill ${STATE.live ? 'is-ok' : 'is-warn'}">
        <i class="fas ${STATE.live ? 'fa-plug' : 'fa-file-code'}"></i>
        ${STATE.live ? 'live /api/terms' : 'committed artifact (read-only)'}
      </span>
      <span class="terms-pill ${stale ? 'is-warn' : ''}"><i class="fas fa-clock"></i> built ${esc(age || 'unknown')}</span>
      <span class="terms-stat">${Number(m.entityCount) || 0} entities</span>
      <span class="terms-stat">${Number(m.termCount) || 0} terms</span>
      <span class="terms-stat${Number(m.droppedCount) ? ' is-warn' : ''}">${Number(m.droppedCount) || 0} dropped at the per-entity cap</span>
      <span class="terms-stat">builder ${esc(m.builderVersion || '?')}</span>
    </div>
    <div class="terms-meta-row terms-meta-sources">${srcRows}</div>
    ${STATE.metaNote ? `<div class="terms-note"><i class="fas fa-circle-info"></i> ${esc(STATE.metaNote)}</div>` : ''}
    ${STATE.emptyServerIndex ? '<div class="terms-note"><i class="fas fa-triangle-exclamation"></i> '
      + 'The term API is up but is holding NO entities, so the search box is running on the committed '
      + 'artifact below. Press Re-ingest artifact to upload it.</div>' : ''}
    ${STATE.live ? '' : '<div class="terms-note"><i class="fas fa-triangle-exclamation"></i> '
      + 'The term API did not answer, so this is the committed artifact with the override seed applied. '
      + 'Edits cannot be saved from here - use Copy override JSON and paste it into '
      + '<code>wwwroot/data/term-overrides.seed.json</code>.</div>'}`;
}

// ---------------------------------------------------------------------------------------------
// ENTITY PICKER
// ---------------------------------------------------------------------------------------------
function renderList() {
  const el = document.getElementById('terms-entities');
  if (!el) return;
  const f = STATE.filter.trim().toLowerCase();
  const rows = STATE.entities.filter((e) => {
    if (!f) return true;
    return (e.entityId + ' ' + (e.name || '') + ' ' + e.entityKind).toLowerCase().includes(f);
  });
  if (!rows.length) { el.innerHTML = '<p class="terms-hint">No entity matches that filter.</p>'; return; }

  el.innerHTML = rows.map((e) => {
    const key = keyOf(e);
    const ov = STATE.overrides[key];
    const curated = ov && ((ov.add || []).length || (ov.suppress || []).length || Object.keys(ov.boost || {}).length);
    const label = (STATE.labels[key] && STATE.labels[key].label) || e.name || e.entityId;
    return `<button type="button" class="terms-ent${STATE.selected === key ? ' is-sel' : ''}" data-key="${esc(key)}">
      <span class="terms-ent-kind terms-kind-${esc(e.entityKind)}">${esc(e.entityKind)}</span>
      <span class="terms-ent-name">${esc(label)}</span>
      <span class="terms-ent-count">${(e.terms || []).length}${curated ? ' <i class="fas fa-user-pen" title="has admin curation"></i>' : ''}</span>
    </button>`;
  }).join('');

  el.querySelectorAll('.terms-ent').forEach((b) => b.addEventListener('click', () => select(b.dataset.key)));
}

function select(key) {
  STATE.selected = key;
  const ov = STATE.overrides[key] || {};
  // Deep-ish copy so an abandoned edit does not mutate what is on screen elsewhere.
  STATE.draft = {
    add: (ov.add || []).map((a) => Object.assign({}, a)),
    suppress: (ov.suppress || []).slice(),
    boost: Object.assign({}, ov.boost || {}),
  };
  renderList();
  renderDetail();
}

// ---------------------------------------------------------------------------------------------
// DETAIL - the parsed terms with their provenance, plus the admin layer on top.
// ---------------------------------------------------------------------------------------------
function renderDetail() {
  const el = document.getElementById('terms-detail');
  if (!el) return;
  if (!STATE.selected) { el.innerHTML = '<p class="terms-hint">Pick an entity on the left to see its parsed terms and their provenance.</p>'; return; }

  const ent = STATE.entities.find((e) => keyOf(e) === STATE.selected);
  if (!ent) { el.innerHTML = '<p class="terms-hint">That entity is no longer in the index. It may have been renamed or removed since the last build.</p>'; return; }

  const d = STATE.draft;
  const suppressed = {};
  d.suppress.forEach((t) => { suppressed[t] = 1; });
  const addedByTerm = {};
  d.add.forEach((a) => { addedByTerm[a.term] = a; });

  // Parsed rows first (they are the record of what the repo actually says), admin rows after.
  const parsedRows = (ent.terms || []).slice()
    .sort((a, b) => (b.weight - a.weight) || String(a.term).localeCompare(String(b.term)))
    .map((t) => {
      const isSup = !!suppressed[t.term];
      const boost = d.boost[t.term];
      const eff = t.weight * (typeof boost === 'number' && boost > 0 ? boost : 1);
      // Provenance count: a term found in three places is stronger evidence than one found once, and
      // the build script keeps ALL of them (spec section 5) rather than collapsing to the winner.
      const provN = Array.isArray(t.prov) ? t.prov.length : 1;
      return `<tr class="${isSup ? 'is-suppressed' : ''}">
        <td class="terms-term">${esc(t.term)}</td>
        <td><span class="terms-origin is-parsed">parsed</span></td>
        <td class="terms-num">${eff.toFixed(2)}${eff !== t.weight ? ` <span class="terms-was">was ${t.weight.toFixed(2)}</span>` : ''}</td>
        <td class="terms-source">${esc(t.source || '')}${provN > 1 ? ` <span class="terms-was">+${provN - 1} more</span>` : ''}</td>
        <td class="terms-file" title="${esc(t.file || '')}">${esc(t.file || '')}</td>
        <td class="terms-actions">
          <input type="number" class="terms-boost" data-term="${esc(t.term)}" min="0" step="0.1"
                 value="${typeof boost === 'number' ? boost : ''}" placeholder="1.0" title="Boost factor applied to the parsed weight">
          <button type="button" class="btn btn-sm terms-sup" data-term="${esc(t.term)}">${isSup ? 'Restore' : 'Suppress'}</button>
        </td>
      </tr>`;
    }).join('');

  const adminRows = d.add.slice().sort((a, b) => String(a.term).localeCompare(String(b.term))).map((a) => `
    <tr class="is-admin">
      <td class="terms-term">${esc(a.term)}</td>
      <td><span class="terms-origin is-admin">admin</span></td>
      <td class="terms-num">${(typeof a.weight === 'number' ? a.weight : 1).toFixed(2)}</td>
      <td class="terms-source">admin</td>
      <td class="terms-file" title="${esc(a.file || '')}">${esc(a.file || 'term_overrides')}</td>
      <td class="terms-actions"><button type="button" class="btn btn-sm terms-del" data-term="${esc(a.term)}">Remove</button></td>
    </tr>`).join('');

  const nav = ent.nav || {};
  const navTxt = [nav.view ? 'view=' + nav.view : '', nav.sel ? 'sel=' + nav.sel : '', nav.href ? 'href=' + nav.href : '']
    .filter(Boolean).join('  ') || 'no route (not shown as a search result)';
  const label = (STATE.labels[STATE.selected] && STATE.labels[STATE.selected].label) || ent.name || ent.entityId;

  el.innerHTML = `
    <div class="terms-detail-head">
      <div>
        <h3>${esc(label)}</h3>
        <div class="terms-key">${esc(STATE.selected)}${ent.tier ? ' &middot; tier ' + esc(ent.tier) : ''}</div>
        <div class="terms-key">${esc(navTxt)}</div>
        ${ent.name && ent.name !== label ? `<div class="terms-key">parsed name: ${esc(ent.name)}</div>` : ''}
      </div>
      <div class="terms-detail-actions">
        <button type="button" class="btn btn-sm" id="btn-terms-copy"><i class="fas fa-copy"></i> Copy override JSON</button>
        <button type="button" class="btn btn-sm" id="btn-terms-clear"><i class="fas fa-eraser"></i> Clear override</button>
        <button type="button" class="btn btn-sm btn-primary" id="btn-terms-save"><i class="fas fa-floppy-disk"></i> Save override</button>
      </div>
    </div>

    ${Number(ent.droppedTermCount) ? `<div class="terms-note"><i class="fas fa-scissors"></i> ${Number(ent.droppedTermCount)}
      lower-weight terms were dropped at the per-entity cap when this entity was parsed. They are logged by the
      build script, not silently discarded - re-run it to see which.</div>` : ''}

    <div class="terms-add">
      <input type="text" id="terms-add-input" placeholder="Add a term a visitor would type (single words; a phrase is entered as several)">
      <input type="number" id="terms-add-weight" min="0" step="0.05" value="1" title="Weight for the added term">
      <button type="button" class="btn btn-sm" id="btn-terms-add"><i class="fas fa-plus"></i> Add</button>
    </div>

    <div class="admin-table-wrap">
      <table class="admin-table terms-table">
        <thead><tr><th>Term</th><th>Origin</th><th>Weight</th><th>Source</th><th>File</th><th></th></tr></thead>
        <tbody>${adminRows}${parsedRows || (adminRows ? '' : '<tr><td colspan="6" class="admin-empty">This entity produced no terms at all. That is a real gap, not an empty state - add the vocabulary a visitor would use.</td></tr>')}</tbody>
      </table>
    </div>`;

  wireDetail();
}

function wireDetail() {
  const el = document.getElementById('terms-detail');
  if (!el) return;

  el.querySelectorAll('.terms-sup').forEach((b) => b.addEventListener('click', () => {
    const t = b.dataset.term;
    const i = STATE.draft.suppress.indexOf(t);
    if (i >= 0) STATE.draft.suppress.splice(i, 1); else STATE.draft.suppress.push(t);
    renderDetail();
  }));

  el.querySelectorAll('.terms-boost').forEach((inp) => inp.addEventListener('change', () => {
    const t = inp.dataset.term;
    const v = parseFloat(inp.value);
    if (!Number.isFinite(v) || v <= 0 || v === 1) delete STATE.draft.boost[t];
    else STATE.draft.boost[t] = v;
    renderDetail();
  }));

  el.querySelectorAll('.terms-del').forEach((b) => b.addEventListener('click', () => {
    STATE.draft.add = STATE.draft.add.filter((a) => a.term !== b.dataset.term);
    renderDetail();
  }));

  const addBtn = document.getElementById('btn-terms-add');
  if (addBtn) addBtn.addEventListener('click', () => {
    const inp = document.getElementById('terms-add-input');
    const wInp = document.getElementById('terms-add-weight');
    const w = parseFloat(wInp && wInp.value);
    const weight = Number.isFinite(w) && w > 0 ? w : 1;
    const toks = tokenizeInput(inp && inp.value);
    if (!toks.length) return;
    toks.forEach((t) => {
      if (STATE.draft.add.some((a) => a.term === t)) return;
      // source is fixed to "admin" and cannot be typed. An admin term that claimed to be parsed would
      // be exactly the disguise spec section 3 forbids.
      STATE.draft.add.push({ term: t, weight, source: 'admin', file: STATIC_SEED_URL.replace(/^\//, 'core/GdbsWeb.Api/wwwroot/') });
    });
    if (inp) inp.value = '';
    renderDetail();
  });

  const copyBtn = document.getElementById('btn-terms-copy');
  if (copyBtn) copyBtn.addEventListener('click', () => {
    const payload = JSON.stringify({ [STATE.selected]: STATE.draft }, null, 2);
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(payload);
    else window.prompt('Copy this into the overrides block of term-overrides.seed.json', payload);
  });

  const saveBtn = document.getElementById('btn-terms-save');
  if (saveBtn) saveBtn.addEventListener('click', () => saveOverride());

  const clearBtn = document.getElementById('btn-terms-clear');
  if (clearBtn) clearBtn.addEventListener('click', () => clearOverride());
}

// ---------------------------------------------------------------------------------------------
// WRITES
// The server is the boundary: it gates on the platform admin role. These calls report exactly what
// came back rather than assuming success, because an override that looks saved and is not would
// quietly send the search box back to the raw parse.
// ---------------------------------------------------------------------------------------------
async function saveOverride() {
  if (!STATE.selected) return;
  const [kind, ...rest] = STATE.selected.split(':');
  const id = rest.join(':');
  try {
    const res = await api.setTermOverride(kind, id, STATE.draft);
    if (res && res.ok === false) { alert('Not saved: ' + (res.reason || 'the server rejected the override.')); return; }
    if (res && res.error) { alert('Not saved: ' + res.error); return; }
    if (res && res.success === false) { alert('Not saved: ' + (res.error || 'the term API did not answer. Use Copy override JSON.')); return; }
    STATE.overrides[STATE.selected] = STATE.draft;
    renderList();
  } catch (e) {
    alert('Not saved: ' + (e && e.message ? e.message : e));
  }
}

async function clearOverride() {
  if (!STATE.selected) return;
  if (!window.confirm('Remove all admin curation for ' + STATE.selected + '? The parsed terms stay.')) return;
  const [kind, ...rest] = STATE.selected.split(':');
  const id = rest.join(':');
  try {
    const res = await api.clearTermOverride(kind, id);
    if (res && (res.error || res.success === false)) { alert('Not cleared: ' + (res.error || 'the term API did not answer.')); return; }
    delete STATE.overrides[STATE.selected];
    STATE.draft = { add: [], suppress: [], boost: {} };
    renderList();
    renderDetail();
  } catch (e) {
    alert('Not cleared: ' + (e && e.message ? e.message : e));
  }
}

// Re-ingest. The build artifact lives in App_Data on the SERVER (it carries provenance paths to gated
// plugin sources and is deliberately never served to the browser), so the server re-reads and ingests
// its own seed - the browser does not fetch or upload it. Deploy a fresh App_Data/term_index_seed.json,
// press this, and the live index refreshes with no restart.
async function rebuild() {
  try {
    const res = await api.reingestTermSeed();
    if (res && res.ok === false) { alert('Re-ingest reported: ' + (res.reason || 'no entities in the seed.')); return; }
    if (res && (res.error || res.success === false)) {
      alert('Re-ingest failed: ' + (res.error || 'the term API did not answer. Confirm the controller is deployed and App_Data/term_index_seed.json is present on the server.'));
      return;
    }
    await load();
  } catch (e) {
    alert('Re-ingest failed: ' + (e && e.message ? e.message : e));
  }
}

export function initAdminTerms() {
  const refresh = document.getElementById('btn-terms-refresh');
  if (refresh) refresh.addEventListener('click', load);
  const reb = document.getElementById('btn-terms-rebuild');
  if (reb) reb.addEventListener('click', rebuild);
  const filter = document.getElementById('terms-filter');
  if (filter) filter.addEventListener('input', () => { STATE.filter = filter.value; renderList(); });
  // Load when the subtab is opened. The panel is hidden at boot and the artifact is several megabytes,
  // so fetching it before anyone asks would tax every admin page load for a tab most never open.
  const tab = document.querySelector('.atab[data-atab="terms"]');
  if (tab) tab.addEventListener('click', () => { if (!STATE.entities.length) setTimeout(load, 50); });
}

export default { initAdminTerms };
